import { action, computed, makeObservable, runInAction } from 'mobx';

import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import AdminNotificationStore, { IAdminNotification } from 'stores/adminNotification/AdminNotificationStore';
import { NotificationType } from 'stores/_shared/Interfaces';
import { destroy, get, getCount, getPage, patch, post } from 'stores/_services/dataService';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import { IDataFetchOptions } from '../../types';
import { SortDirection } from 'constants/SortDirection';

const SEARCH_FIELDS = ['name', 'recipients', 'subject'];

export default class AdminNotificationListStore extends BaseListStore<AdminNotificationStore> {
    constructor() {
        super({
            itemStore: AdminNotificationStore
        });

        makeObservable(this, {
            notificationSettings: computed,
            getAdminNotification: action,
            getAdminNotifications: action,
            saveNotification: action
        });

        this.sortBy = 'updatedOn';
        this.sortDirection = SortDirection.DESC;
    }

    get notificationSettings(): IAdminNotification {
        return {
            notificationType: NotificationType.ALERT_DIGEST,
            schedule: '0 8 * * *',
            enabled: true
        };
    }

    async getAdminNotification(notificationId: string): Promise<void> {
        const { data } = await get(`/scheduledAdminNotifications/${notificationId}`);
        data.recipients = data?.recipients?.split('\n') || [];
        this._updateData(data);
    }

    async getAdminNotifications(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort,
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    },
                    {
                        relation: 'editor',
                        scope: {
                            fields: ['email']
                        }
                    },
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage('scheduledAdminNotifications', options);
        const totalCount = await getCount('scheduledAdminNotifications', { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        data.map((item: any) => (item.recipients = item?.recipients?.split('\n') || []));

        this._updateData(data);
        this._updateIds(data, 0);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async saveNotification(payload: any): Promise<IAdminNotification | undefined> {
        payload.recipients = payload.recipients?.join('\n');

        const { data } = await patch('/scheduledAdminNotifications', payload);

        data.recipients = data?.recipients?.split('\n') || [];

        return data;
    }

    async toggleEnabled(notificationId: string): Promise<void> {
        const notification = this.data.get(notificationId)?.notification;

        const payload = {
            id: notificationId,
            enabled: !notification?.enabled
        };

        await post('scheduledAdminNotifications/toggleEnabled', payload);
        await this.getAdminNotification(notificationId);
    }

    async deleteNotification(notificationId: string): Promise<void> {
        await destroy(`scheduledAdminNotifications/${notificationId}`);
        await this.getAdminNotifications({ reset: true }, 1, this.pageSize);
    }

    async deleteNotifications(id?: string): Promise<void> {
        const conditions = getListConditions(id ? [id] : this.selectedIdList, this.filter);

        const options = {
            searchFields: SEARCH_FIELDS,
            where: conditions
        };

        await destroy('scheduledAdminNotifications', options);

        this.selectedIds = {};
        await this.getAdminNotifications({ reset: true }, 1, this.pageSize);
    }
}
